<template>
  <div class="notifications-page">
    <div class="notifications-header">
      <div>
        <h1 class="notification-title">All Notifications</h1>
        <p class="notification-subtitle">
          {{ unreadSummary }}
        </p>
      </div>

      <div class="notifications-toolbar">
        <b-form-select
          v-model="readFilter"
          :options="readFilterOptions"
          class="toolbar-select"
          @change="handleFilterChange"
        />
        <b-button
          v-if="unreadCount"
          variant="primary"
          class="mark-all-read-btn"
          :disabled="isMarkingAll"
          @click="markAllRead"
        >
          {{ isMarkingAll ? 'Marking...' : 'Mark all read' }}
        </b-button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="state-panel"
    >
      <b-spinner label="Loading notifications..." />
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      class="mb-2"
    >
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <span>{{ loadError }}</span>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="fetchNotifications"
        >
          Retry
        </b-button>
      </div>
    </b-alert>

    <div
      v-else-if="!notifications.length"
      class="state-panel state-panel--empty"
    >
      <feather-icon icon="BellOffIcon" size="42" class="mb-1" />
      <h4 class="mb-50">No notifications found</h4>
      <p class="mb-0">
        There are no notifications for the selected filter.
      </p>
    </div>

    <div v-else class="notification-list">
      <button
        v-for="item in notifications"
        :key="item.id"
        type="button"
        class="notification-row"
        :class="{ 'notification-row--unread': !item.isRead }"
        @click="markNotificationRead(item)"
      >
        <div class="notification-main">
          <NotificationAvatar
            :image="item.image"
            :initials="item.initials"
            :alt="item.title"
            :size="42"
          />

          <div class="notification-copy">
            <div class="notification-topline">
              <h5 class="mb-0 notification-name">{{ item.title }}</h5>
              <span
                class="notification-status"
                :class="{ 'notification-status--read': item.isRead }"
              >
                {{ item.isRead ? 'Read' : 'Unread' }}
              </span>
            </div>
            <p class="mb-0 notification-text">{{ item.message || 'No message provided.' }}</p>
            <small class="notification-time">{{ item.timeAgo || item.createdAt }}</small>
          </div>
        </div>

        <div class="notification-aside">
          <span v-if="pendingIds[item.id]" class="notification-pending">Saving...</span>
          <span v-else-if="!item.isRead" class="dot" />
        </div>
      </button>
    </div>

    <AdminPaginationBar
      :page="page"
      :per-page="perPage"
      :total="pagination.totalRecords"
      @change="onPageChange"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import NotificationAvatar from '@/components/admin/NotificationAvatar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  ADMIN_NOTIFICATIONS_UPDATED,
  normalizeNotificationsResponse,
  notificationReadFilterOptions,
} from '@/utils/adminNotifications'
import { notifyError } from '@/utils/uiActions'

export default {
  components: {
    BAlert,
    BButton,
    BFormSelect,
    BSpinner,
    AdminPaginationBar,
    NotificationAvatar,
  },
  data() {
    return {
      isLoading: false,
      isMarkingAll: false,
      loadError: '',
      notifications: [],
      unreadCount: 0,
      readFilter: 'all',
      readFilterOptions: notificationReadFilterOptions,
      perPage: 10,
      page: 1,
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalRecords: 0,
      },
      pendingIds: {},
    }
  },
  computed: {
    unreadSummary() {
      if (!this.unreadCount) return 'All notifications are read.'
      return `${this.unreadCount} unread notification${this.unreadCount === 1 ? '' : 's'}`
    },
  },
  created() {
    this.fetchNotifications()
    this.$root.$on(ADMIN_NOTIFICATIONS_UPDATED, this.handleNotificationsUpdated)
  },
  beforeDestroy() {
    this.$root.$off(ADMIN_NOTIFICATIONS_UPDATED, this.handleNotificationsUpdated)
  },
  methods: {
    onPageChange(nextPage) {
      this.page = nextPage
      this.fetchNotifications()
    },
    handleNotificationsUpdated(payload = {}) {
      if (typeof payload.unreadCount === 'number') {
        this.unreadCount = payload.unreadCount
      }

      this.fetchNotifications()
    },
    async fetchNotifications() {
      this.isLoading = true
      this.loadError = ''

      try {
        const params = {
          view: 'all',
          page: this.page,
          limit: this.perPage,
        }

        if (this.readFilter !== 'all') {
          params.is_read = this.readFilter
        }

        const response = await adminPanelApi.notifications(params)
        const normalized = normalizeNotificationsResponse(response.data || {})

        this.notifications = normalized.notifications
        this.unreadCount = normalized.unreadCount
        this.pagination = normalized.pagination

        if (this.pagination.currentPage && this.pagination.currentPage !== this.page) {
          this.page = this.pagination.currentPage
        }
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Unable to load notifications.'
        this.notifications = []
        this.pagination = {
          currentPage: 1,
          totalPages: 0,
          totalRecords: 0,
        }
      } finally {
        this.isLoading = false
      }
    },
    handleFilterChange() {
      this.page = 1
      this.fetchNotifications()
    },
    async markNotificationRead(item) {
      if (!item?.id || item.isRead || this.pendingIds[item.id]) return

      this.$set(this.pendingIds, item.id, true)

      try {
        await adminPanelApi.notificationsMarkRead({ notification_ids: [item.id] })
        this.$root.$emit(ADMIN_NOTIFICATIONS_UPDATED, {
          unreadCount: Math.max(0, this.unreadCount - 1),
        })
      } catch (error) {
        notifyError(this, error.response?.data?.message || 'Unable to update the notification.')
      } finally {
        this.$delete(this.pendingIds, item.id)
      }
    },
    async markAllRead() {
      if (!this.unreadCount || this.isMarkingAll) return

      this.isMarkingAll = true

      try {
        await adminPanelApi.notificationsMarkRead({ mark_all: true })
        this.$root.$emit(ADMIN_NOTIFICATIONS_UPDATED, { unreadCount: 0 })
      } catch (error) {
        notifyError(this, error.response?.data?.message || 'Unable to mark notifications as read.')
      } finally {
        this.isMarkingAll = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.notifications-page {
  width: 100%;
}

.notifications-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.notification-title {
  font-size: 2.3rem;
  font-weight: 700;
  color: #1f1f29;
  margin-bottom: 0.25rem;
}

.notification-subtitle {
  color: #6e6b7b;
  margin-bottom: 0;
}

.notifications-toolbar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.75rem;
  flex-wrap: nowrap;
}

.toolbar-select {
  min-width: 200px;
  width: 200px;
}

.mark-all-read-btn {
  min-width: 140px;
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notification-row {
  width: 100%;
  border: none;
  border-radius: 0.6rem;
  background: #fff;
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;
  transition: box-shadow 0.2s ease;
}

.notification-row:hover {
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.notification-row--unread {
  background: rgba(115, 103, 240, 0.04);
}

.notification-main {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  min-width: 0;
  flex: 1;
}

::v-deep .notification-avatar-wrap {
  flex-shrink: 0;
}

.notification-copy {
  min-width: 0;
  flex: 1;
}

.notification-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.notification-name {
  font-size: 1.05rem;
  font-weight: 600;
  color: #2f2b3d;
}

.notification-status {
  color: #011425;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.notification-status--read {
  color: #6e6b7b;
}

.notification-text {
  font-size: 0.95rem;
  color: #6e6b7b;
  line-height: 1.45;
  margin-top: 0.3rem;
}

.notification-time,
.notification-pending {
  color: #b9b9c3;
  font-size: 0.82rem;
  margin-top: 0.4rem;
}

.notification-aside {
  min-width: 48px;
  display: flex;
  justify-content: flex-end;
  padding-top: 0.35rem;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #011425;
  display: inline-block;
}

.state-panel {
  min-height: 260px;
  border: 1px dashed #d9d6e5;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  color: #6e6b7b;
}

.state-panel--empty {
  text-align: center;
}

.pagination-wrap {
  margin-top: 1.25rem;
}

@media (max-width: 767.98px) {
  .notifications-header {
    flex-direction: column;
  }

  .notifications-toolbar {
    width: 100%;
    flex-wrap: wrap;
  }

  .toolbar-select {
    width: 100%;
    min-width: 0;
  }

  .mark-all-read-btn {
    width: 100%;
    min-width: 0;
  }

  .notification-row {
    flex-direction: column;
  }

  .notification-aside {
    width: 100%;
    justify-content: flex-start;
    padding-top: 0;
  }
}
</style>
